import { Check, ShieldCheck, X, XCircle } from "lucide-react";
import type { ApprovalDecision, ApprovalRecord } from "../types";

interface ApprovalPromptProps {
  approval: ApprovalRecord;
  busy: boolean;
  context?: "chat" | "panel";
  onDecision(decision: ApprovalDecision): void;
}

export function ApprovalPrompt({
  approval,
  busy,
  context = "panel",
  onDecision,
}: ApprovalPromptProps) {
  return (
    <article
      className={`approval-card approval-prompt ${context === "chat" ? "chat" : "panel"}`}
      aria-label="Validation requise"
    >
      <header className="approval-prompt-heading">
        <span><ShieldCheck aria-hidden="true" /></span>
        <div>
          <small>Validation requise</small>
          <strong>{approval.title}</strong>
          <p>{approval.reason || approvalKindLabel(approval)}</p>
        </div>
      </header>
      <div className="approval-actions">
        {isApprovalDecisionAllowed(approval, "accept") ? (
          <button type="button" className="text-command primary" disabled={busy} onClick={() => onDecision("accept")}>
            <Check aria-hidden="true" />Autoriser
          </button>
        ) : null}
        {isApprovalDecisionAllowed(approval, "accept_session") ? (
          <button type="button" className="text-command" disabled={busy} onClick={() => onDecision("accept_session")}>
            Pour la session
          </button>
        ) : null}
        {isApprovalDecisionAllowed(approval, "decline") ? (
          <button type="button" className="text-command danger" disabled={busy} onClick={() => onDecision("decline")}>
            <XCircle aria-hidden="true" />Refuser
          </button>
        ) : null}
        {hasExplicitApprovalDecision(approval, "cancel") ? (
          <button type="button" className="text-command" disabled={busy} onClick={() => onDecision("cancel")}>
            <X aria-hidden="true" />Annuler
          </button>
        ) : null}
      </div>
    </article>
  );
}

export function approvalDecisionNotice(decision: ApprovalDecision): string {
  if (decision.startsWith("accept")) return "Action autorisée.";
  return decision === "cancel" ? "Action annulée." : "Action refusée.";
}

function approvalKindLabel(approval: ApprovalRecord): string {
  if (approval.kind === "command") return "Commande proposée par Nora";
  if (approval.kind === "file_change") return "Modification de fichiers";
  return "Extension de permissions";
}

function isApprovalDecisionAllowed(approval: ApprovalRecord, decision: ApprovalDecision): boolean {
  if (approval.kind !== "command") return true;
  const available = approval.requestPayload.availableDecisions;
  if (!Array.isArray(available) || available.length === 0) return true;
  const providerDecision = decision === "accept_session" ? "acceptForSession" : decision;
  return available.some((value) => value === providerDecision);
}

function hasExplicitApprovalDecision(approval: ApprovalRecord, decision: ApprovalDecision): boolean {
  const available = approval.requestPayload.availableDecisions;
  const providerDecision = decision === "accept_session" ? "acceptForSession" : decision;
  return Array.isArray(available) && available.some((value) => value === providerDecision);
}
